import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { Panel, PanelHead } from '../components/primitives/Panel';
import { FansReleasesPage } from './FansReleasesPage';
import { fansMessages as messages } from './fansMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansReleasesPanelProps {
  accountId: number;
}

/**
 * "Releases sent to your fans", newest first (design c1). Each loaded page keeps its own query, so
 * `presave.release.updated` refreshes every page on screen. The list loads by itself and shows its
 * own LoadingView (design c3).
 */
const FansReleasesPanelComponent: React.FC<FansReleasesPanelProps> = ({ accountId }) => {
  const intl = useIntl();
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const loadMore = useCallback(
    (cursor: string) => setCursors((current) => (current.includes(cursor) ? current : [...current, cursor])),
    []
  );

  const pages = useMemo(
    () =>
      cursors.map((cursor, index) => (
        <FansReleasesPage
          key={cursor ?? 'first'}
          accountId={accountId}
          cursor={cursor}
          onLoadMore={index === cursors.length - 1 ? loadMore : undefined}
        />
      )),
    [accountId, cursors, loadMore]
  );

  return (
    <Panel>
      <PanelHead
        title={intl.formatMessage(messages.releasesTitle)}
        right={<span className={styles.meta}>{intl.formatMessage(messages.releasesMeta)}</span>}
      />
      <div className={styles.releases}>
        <div className={styles.releaseHead}>
          <span />
          <span>{intl.formatMessage(messages.colRelease)}</span>
          <span className={styles.releaseNumber}>{intl.formatMessage(messages.colSaved)}</span>
          <span className={styles.releaseNumber}>{intl.formatMessage(messages.colEmailed)}</span>
          <span>{intl.formatMessage(messages.colNotSaved)}</span>
        </div>
        {pages}
      </div>
    </Panel>
  );
};

export const FansReleasesPanel = TypedMemo(FansReleasesPanelComponent);
